import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { CallButton } from "@/components/CallButton";
import { WA, PHONE } from "@/content";
import { scrollToId } from "@/lib/scroll";

const NAV = [
  { label: "Beneficios", href: "#beneficios" },
  { label: "Especies", href: "#especies" },
  { label: "Ficha técnica", href: "#specs" },
  { label: "Lanzamiento", href: "#lanzamiento" },
  { label: "Soporte", href: "#soporte" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass shadow-[0_1px_0_0_rgba(226,232,240,0.9)]" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => go(e, n.href)}
                className="text-[13.5px] font-semibold text-slate-600 hover:text-brand-orange transition-colors duration-300"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={PHONE.tel}
              data-testid="header-call-cta"
              aria-label={`Llamar al ${PHONE.intl}`}
              title={`Llamar al ${PHONE.intl}`}
              className="group inline-flex h-10 items-center gap-2 rounded-full border border-slate-300 bg-white/80 px-3 sm:px-4 text-[13.5px] font-bold text-brand-ink hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              <span className="hidden md:inline font-mono2 text-xs">{PHONE.display}</span>
            </a>
            <a
              href={WA.quote}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-cta"
              className="btn-shine hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-ink text-white text-[13.5px] font-bold px-5 py-2.5 hover:bg-brand-orange transition-colors duration-300"
            >
              <MessageCircle className="h-4 w-4" />
              Cotizar por WhatsApp
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Abrir menú"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-brand-ink"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-brand-ink dotgrid-light pt-24 px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.href}
                  href={n.href}
                  onClick={(e) => go(e, n.href)}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  className="font-display text-4xl font-extrabold text-white py-3 border-b border-white/10"
                >
                  {n.label}
                </motion.a>
              ))}
              <a
                href={WA.quote}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange text-white font-bold px-6 py-4"
              >
                <MessageCircle className="h-5 w-5" />
                Cotizar por WhatsApp
              </a>
              <CallButton variant="dark" testid="mobile-menu-call-cta" className="mt-3" />
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
